import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ThemeToggle from './ThemeToggle';
import { AiOutlineClose, AiOutlineMenu } from 'react-icons/ai';
import { IoChevronDownOutline } from 'react-icons/io5';
import { BookOpen, Brain, FileCode2, FlaskConical, Search, LayoutDashboard, Sparkles } from 'lucide-react';
import { FaUser, FaSignOutAlt, FaCog, FaDatabase, FaChartBar, FaChartPie, FaSearch, FaCreditCard, FaCar } from 'react-icons/fa';
import logo from '../assets/logo.png';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { blogPosts } from '../data/blogPosts';

// ─── OS detection for keyboard hints ────────────────────────────────────────
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const kbdShortcut = isMac ? '⌘K' : 'Ctrl K';

// ─── Searchable site content ─────────────────────────────────────────────────
const SEARCH_ITEMS = [
  { title: 'Home',                        description: 'Back to the homepage',                           route: '/',                             category: 'Pages',      icon: 'home' },
  { title: 'SQL Track',                   description: 'Business SQL case studies in e-commerce, SaaS, fintech and product analytics', route: '/exercises/thinking-in-data', category: 'Pages', icon: 'db' },
  { title: 'Data Viz Track',              description: 'Learn which chart to use and why - five hands-on modules with real datasets', route: '/exercises/data-viz', category: 'Pages', icon: 'bar' },
  { title: 'Blog',                        description: 'Guides and insights for data careers',           route: '/resources/blog',               category: 'Pages',      icon: 'blog' },
  { title: 'ML Guides',                   description: 'Learn machine learning concepts step by step',   route: '/machine-learning',             category: 'Pages',      icon: 'ml' },
  { title: 'SQL Cheatsheet',              description: 'Quick reference for SQL syntax',                 route: '/resources/sql-cheatsheet',     category: 'Pages',      icon: 'sql' },
  { title: 'Data Sandbox',               description: 'Query your own CSV files in-browser',            route: '/data-sandbox',                  category: 'Pages',      icon: 'sandbox' },
  { title: 'About',                       description: 'Learn about DataHelix',                          route: '/about',                        category: 'Pages',      icon: 'about' },
  { title: 'SQL Fundamentals',            description: 'SELECT, WHERE, ORDER BY - core SQL building blocks', route: '/exercises/thinking-in-data/case/0', category: 'SQL',   icon: 'db' },
  { title: 'Filtering & Aggregation',     description: 'GROUP BY, HAVING, COUNT, SUM and more',         route: '/exercises/thinking-in-data/case/1', category: 'SQL',   icon: 'db' },
  { title: 'JOINs & Relationships',       description: 'INNER, LEFT, RIGHT JOIN across tables',         route: '/exercises/thinking-in-data/case/2', category: 'SQL',   icon: 'db' },
  { title: 'CTEs & Subqueries',           description: 'WITH clauses, nested queries, query structure', route: '/exercises/thinking-in-data/case/3', category: 'SQL',   icon: 'db' },
  { title: 'Window Functions',            description: 'ROW_NUMBER, RANK, LAG, LEAD analytics',         route: '/exercises/thinking-in-data/case/4', category: 'SQL',   icon: 'db' },
  { title: 'Fintech Analytics',           description: 'Transactions, fraud patterns, real finance data', route: '/exercises/thinking-in-data/case/5', category: 'SQL', icon: 'db' },
  { title: 'Foundations: Core Chart Types', description: 'Bar, Line, Pie, Stacked Bar and Area charts', route: '/exercises/data-viz-intro',     category: 'Data Viz',   icon: 'bar' },
  { title: 'Beyond the Basics',           description: 'Histogram, Heatmap, Map, Box Plot, Scatter',    route: '/exercises/data-viz-intermediate', category: 'Data Viz', icon: 'pie' },
  { title: 'Is Growth Slowing Down?',     description: 'Investigate UK product growth with real data',  route: '/exercises/data-viz-1',         category: 'Data Viz',   icon: 'search' },
  { title: 'Customer Financial Behavior', description: 'Analyze spending, income, and transaction patterns', route: '/exercises/data-viz-2',    category: 'Data Viz',   icon: 'finance' },
  { title: 'Ride-Sharing Operations',     description: 'City-level demand, revenue and driver analysis', route: '/exercises/data-viz-3',        category: 'Data Viz',   icon: 'car' },
  { title: 'My Dashboard',               description: 'View your progress, streaks and completions',    route: '/account',                      category: 'Account',    icon: 'user' },
  { title: 'Account Settings',           description: 'Update your name, password and profile photo',   route: '/account?tab=settings',         category: 'Account',    icon: 'settings' },
];

// ─── Blog posts as searchable items ─────────────────────────────────────────
const BLOG_SEARCH_ITEMS = blogPosts.map(post => ({
  title: post.title,
  description: post.excerpt.length > 88 ? post.excerpt.substring(0, 88) + '...' : post.excerpt,
  route: `/resources/blog/${post.slug}`,
  category: 'Blog',
  icon: 'blog',
}));

const ALL_SEARCH_ITEMS = [...SEARCH_ITEMS, ...BLOG_SEARCH_ITEMS];

function SearchIcon({ type, size = 14 }) {
  if (type === 'db')       return <FaDatabase size={size} className="text-blue-400" />;
  if (type === 'bar')      return <FaChartBar size={size} className="text-orange-400" />;
  if (type === 'pie')      return <FaChartPie size={size} className="text-purple-400" />;
  if (type === 'search')   return <FaSearch size={size} className="text-emerald-400" />;
  if (type === 'finance')  return <FaCreditCard size={size} className="text-cyan-400" />;
  if (type === 'car')      return <FaCar size={size} className="text-rose-400" />;
  if (type === 'user')     return <FaUser size={size} className="text-orange-400" />;
  if (type === 'settings') return <FaCog size={size} className="text-gray-400" />;
  if (type === 'blog')     return <BookOpen size={size} className="text-amber-400" />;
  if (type === 'ml')       return <Brain size={size} className="text-violet-400" />;
  if (type === 'sql')      return <FileCode2 size={size} className="text-sky-400" />;
  if (type === 'sandbox')  return <FlaskConical size={size} className="text-teal-400" />;
  return <Search size={size} className="text-gray-400" />;
}

// ─── Search Modal ─────────────────────────────────────────────────────────────
function SearchModal({ onClose }) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  const results = useMemo(() => {
    if (!query.trim()) return SEARCH_ITEMS.slice(0, 8);
    const q = query.toLowerCase();
    return ALL_SEARCH_ITEMS.filter(item =>
      item.title.toLowerCase().includes(q) ||
      item.description.toLowerCase().includes(q) ||
      item.category.toLowerCase().includes(q)
    ).slice(0, 12);
  }, [query]);

  useEffect(() => { setActiveIdx(0); }, [results]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const go = useCallback((item) => {
    navigate(item.route);
    onClose();
  }, [navigate, onClose]);

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && results[activeIdx]) go(results[activeIdx]);
    if (e.key === 'Escape') onClose();
  };

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  const categories = [...new Set(results.map(r => r.category))];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[100000] flex items-start justify-center pt-[10vh] px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
      <div
        className="relative w-full max-w-2xl bg-white dark:bg-[#0d1523] rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.4)] border border-gray-200/80 dark:border-white/8 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-white/6">
          <Search size={18} className="text-orange-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search courses, modules, resources..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            className="flex-1 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 text-base focus:outline-none"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-0.5">
              <AiOutlineClose size={14} />
            </button>
          )}
          <kbd className="hidden sm:flex items-center text-[10px] text-gray-400 bg-gray-100 dark:bg-white/8 px-2 py-1 rounded-md font-mono border border-gray-200 dark:border-white/10">Esc</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[58vh] overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <Search size={28} className="text-gray-300 dark:text-gray-700 mx-auto mb-3" />
              <div className="text-sm font-medium text-gray-400">No results for "{query}"</div>
              <div className="text-xs text-gray-400 dark:text-gray-600 mt-1">Try a different search term</div>
            </div>
          ) : (
            <div className="py-2">
              {categories.map((cat, catIdx) => (
                <div key={cat} className={catIdx > 0 ? 'mt-1' : ''}>
                  <div className="flex items-center gap-2 px-5 pt-3.5 pb-1.5">
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-600">{cat}</span>
                    <div className="flex-1 h-px bg-gray-100 dark:bg-white/5" />
                  </div>
                  {results.filter(r => r.category === cat).map((item) => {
                    const idx = results.indexOf(item);
                    const isActive = idx === activeIdx;
                    return (
                      <button
                        key={item.route}
                        data-active={isActive ? 'true' : undefined}
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => go(item)}
                        className={`w-full flex items-center gap-3.5 px-5 py-3 text-left transition-all relative ${
                          isActive
                            ? 'bg-orange-50 dark:bg-orange-500/8'
                            : 'hover:bg-gray-50 dark:hover:bg-white/4'
                        }`}
                      >
                        {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-orange-500 rounded-r-full" />}
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
                          isActive ? 'bg-orange-100 dark:bg-orange-500/15' : 'bg-gray-100 dark:bg-white/8'
                        }`}>
                          <SearchIcon type={item.icon} size={15} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm font-semibold truncate ${
                            isActive ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-white'
                          }`}>{item.title}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-500 truncate mt-0.5">{item.description}</div>
                        </div>
                        {isActive && (
                          <div className="flex-shrink-0 text-orange-400">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 dark:border-white/6 flex items-center gap-4 text-[11px] text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-white/2">
          <span className="flex items-center gap-1.5"><kbd className="bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 px-1.5 py-0.5 rounded font-mono">↑↓</kbd> navigate</span>
          <span className="flex items-center gap-1.5"><kbd className="bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 px-1.5 py-0.5 rounded font-mono">↵</kbd> open</span>
          <span className="flex items-center gap-1.5"><kbd className="bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 px-1.5 py-0.5 rounded font-mono">Esc</kbd> close</span>
          <span className="ml-auto flex items-center gap-1.5"><kbd className="bg-white dark:bg-white/10 border border-gray-200 dark:border-white/10 px-1.5 py-0.5 rounded font-mono">{kbdShortcut}</kbd> to open</span>
        </div>
      </div>
    </div>,
    document.body
  );
}


const Navbar = () => {
  const { user, signOut } = useAuth();
  const auth = Boolean(user);
  const [nav, setNav] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const timeoutRef = useRef(null);
  const navRef = useRef(null);
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [resourcesTimeout, setResourcesTimeout] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Cmd+K / Ctrl+K to open search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const avatarUrl = user?.user_metadata?.avatar_url || null;
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || '';
  const initials = displayName.split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (nav) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [nav]);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleNav = useCallback(() => {
    setNav(prev => {
      if (prev) {
        setMobileResourcesOpen(false);
      }
      return !prev;
    });
  }, []);

  // Memoized mobile toggle functions
  const toggleMobileResources = useCallback(() => {
    setMobileResourcesOpen(prev => !prev);
  }, []);

  // Navigation handler
  const handleNavigate = (e, path) => {
    e.preventDefault();
    e.stopPropagation();
    clearTimeout(timeoutRef.current);
    setResourcesOpen(false);
    setNav(false);
    navigate(path);
  };

  // Memoized navigation for mobile
  const navigateTo = useCallback((path) => {
    setNav(false);
    setMobileResourcesOpen(false);
    navigate(path);
  }, [navigate]);

  // Check if a path is active
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isResourcesActive = () => {
    return ['/machine-learning', '/resources', '/cheatsheet', '/data-sandbox'].some(path => location.pathname.includes(path));
  };

  // Helper for Desktop Nav Links
  const NavLink = ({ to, children }) => {
    const active = isActive(to);
    return (
      <Link
        to={to}
        className={`relative px-2 py-1 transition-all duration-300 group ${active ? 'text-[#f97316]' : 'text-gray-300 hover:text-white'}`}
      >
        {children}
        <span className={`absolute bottom-0 left-0 h-0.5 bg-[#f97316] transition-all duration-300 ${active ? 'w-full shadow-[0_0_10px_#f97316]' : 'w-0 group-hover:w-full'}`}></span>
      </Link>
    );
  };

  // Mobile Menu Portal Component
  const MobileMenuPortal = () => {
    return ReactDOM.createPortal(
      <>
        {/* Mobile Menu Background Overlay */}
        <div
          className={`fixed inset-0 bg-black/60 backdrop-blur-sm ease-in-out duration-300 ${nav ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
          style={{ zIndex: 99998 }}
          onClick={handleNav}
        ></div>

        {/* Mobile Menu Panel */}
        <div
          className={`fixed left-0 top-0 bottom-0 w-[85%] max-w-sm bg-[#0a1220]/95 backdrop-blur-xl border-r border-[#f97316]/20 shadow-[0_0_50px_rgba(0,0,0,0.5)] ease-[cubic-bezier(0.16,1,0.3,1)] duration-500 overflow-hidden ${nav ? 'translate-x-0' : 'translate-x-[-100%]'}`}
          style={{ zIndex: 99999 }}
        >
          {/* Mobile Header */}
          <div className="flex justify-between items-center m-6 border-b border-gray-800 pb-6">
            <Link to="/" className="flex items-center" onClick={() => setNav(false)}>
              <img src={logo} alt="Logo" className="h-8 w-auto mr-3" />
              <h1 className='text-2xl font-bold bg-gradient-to-r from-[#f97316] to-[#fb923c] inline-block text-transparent bg-clip-text tracking-wide'>
                DataHelix
              </h1>
            </Link>
            <div className="flex items-center gap-4">
              <ThemeToggle />
              <AiOutlineClose
                size={22}
                className="text-[#f97316] cursor-pointer hover:rotate-90 transition-all duration-300"
                onClick={handleNav}
              />
            </div>
          </div>

          {/* Mobile Menu Items */}
          <div className="overflow-y-auto h-[calc(100%-100px)] px-2">
            <ul className='text-gray-300 space-y-2'>
              <li>
                <Link
                  to="/"
                  className={`block p-4 rounded-xl transition-all duration-300 ${isActive('/') ? 'bg-[#f97316]/10 text-[#f97316] border border-[#f97316]/20' : 'hover:bg-white/5'}`}
                  onClick={() => setNav(false)}
                >
                  Home
                </Link>
              </li>

              <li>
                <Link
                  to="/exercises"
                  className={`block p-4 rounded-xl transition-all duration-300 ${isActive('/exercises') ? 'bg-[#f97316]/10 text-[#f97316] border border-[#f97316]/20' : 'hover:bg-white/5'}`}
                  onClick={() => setNav(false)}
                >
                  Courses
                </Link>
              </li>

              {/* Mobile Resources Menu */}
              <li>
                <div
                  className={`flex justify-between items-center p-4 rounded-xl transition-all duration-300 cursor-pointer ${isResourcesActive() ? 'text-[#f97316]' : 'hover:bg-white/5'}`}
                  onClick={toggleMobileResources}
                >
                  <span>Resources</span>
                  <IoChevronDownOutline
                    className={`transition-transform duration-300 ${mobileResourcesOpen ? 'rotate-180' : 'rotate-0'}`}
                  />
                </div>

                {/* Mobile Resources Submenu */}
                <div
                  className={`overflow-hidden transition-all duration-300 ${mobileResourcesOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <ul className="pl-2 space-y-1 mt-1">
                    <li>
                      <div
                        className={`flex items-center gap-3 p-3 rounded-lg text-sm transition-all cursor-pointer ${isActive('/resources/blog') ? 'text-[#f97316]' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                        onClick={() => navigateTo('/resources/blog')}
                      >
                        <BookOpen size={16} />
                        <span>Blog</span>
                      </div>
                    </li>
                    <li>
                      <div
                        className={`flex items-center gap-3 p-3 rounded-lg text-sm transition-all cursor-pointer ${isActive('/machine-learning') ? 'text-[#f97316]' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                        onClick={() => navigateTo('/machine-learning')}
                      >
                        <Brain size={16} />
                        <span>Machine Learning Guides</span>
                      </div>
                    </li>
                    <li>
                      <div
                        className={`flex items-center gap-3 p-3 rounded-lg text-sm transition-all cursor-pointer ${isActive('/resources/sql-cheatsheet') ? 'text-[#f97316]' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                        onClick={() => navigateTo('/resources/sql-cheatsheet')}
                      >
                        <FileCode2 size={16} />
                        <span>SQL Cheatsheet</span>
                      </div>
                    </li>
                    <li>
                      <div
                        className={`flex items-center gap-3 p-3 rounded-lg text-sm transition-all cursor-pointer ${isActive('/data-sandbox') ? 'text-[#f97316]' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                        onClick={() => navigateTo('/data-sandbox')}
                      >
                        <FlaskConical size={16} />
                        <span>Data Sandbox</span>
                      </div>
                    </li>
                  </ul>
                </div>
              </li>

              <li>
                <Link
                  to="/about"
                  className={`block p-4 rounded-xl transition-all duration-300 ${isActive('/about') ? 'bg-[#f97316]/10 text-[#f97316] border border-[#f97316]/20' : 'hover:bg-white/5'}`}
                  onClick={() => setNav(false)}
                >
                  About
                </Link>
              </li>

              <li className="pt-6 border-t border-gray-800 mt-6">
                {auth ? (
                  <div className="space-y-3">
                    <Link
                      to="/account"
                      className={`block p-4 rounded-xl text-center transition-all duration-300 ${isActive('/account') ? 'bg-[#f97316]/10 text-[#f97316] border border-[#f97316]/20' : 'bg-gray-800 text-white'}`}
                      onClick={() => setNav(false)}
                    >
                      Account
                    </Link>
                    <button
                      onClick={async () => {
                        try {
                          await signOut();
                        } catch (error) {
                          console.error("Logout error:", error);
                        } finally {
                          setNav(false);
                          navigate('/');
                        }
                      }}
                      className="block w-full p-4 rounded-xl border border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <Link
                      to="/login"
                      className="block p-4 text-center rounded-xl border border-gray-700 text-gray-300 hover:bg-gray-800 transition-all"
                      onClick={() => setNav(false)}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="block p-4 text-center rounded-xl bg-gradient-to-r from-[#f97316] to-[#fb923c] text-white font-bold shadow-lg shadow-orange-900/20"
                      onClick={() => setNav(false)}
                    >
                      Sign Up
                    </Link>
                  </div>
                )}
              </li>
            </ul>
          </div>
        </div>
      </>,
      document.body
    );
  };

  // Show and hide resources with delay
  const handleResourcesMouseEnter = () => {
    clearTimeout(resourcesTimeout);
    setResourcesOpen(true);
  };

  const handleResourcesMouseLeave = () => {
    const timeout = setTimeout(() => {
      setResourcesOpen(false);
    }, 100);
    setResourcesTimeout(timeout);
  };

  return (
    <>
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
      <div className='sticky top-0 z-50 transition-all duration-300'>
        {/* Glassmorphism Background */}
        <div className="absolute inset-0 bg-[#0a1220]/80 backdrop-blur-md border-b border-white/5 shadow-lg"></div>

        <div className='relative flex items-center h-16 max-w-[1400px] mx-auto px-6 text-white' ref={navRef}>

          {/* Logo + Brand Name */}
          <Link to="/" className="flex items-center cursor-pointer group mr-12">
            <img
              src={logo}
              alt="Logo"
              className="h-9 w-auto mr-3 transition-transform duration-500 group-hover:rotate-[360deg]"
            />
            <h1 className='text-2xl font-bold bg-gradient-to-r from-[#f97316] to-[#fb923c] inline-block text-transparent bg-clip-text tracking-wide'>
              DataHelix
            </h1>
          </Link>

          {/* Desktop Menu */}
          <ul className='hidden md:flex items-center gap-1 flex-1'>
            <li><NavLink to="/">Home</NavLink></li>

            <li>
              <Link
                to="/exercises"
                className={`relative px-4 py-2 mx-2 rounded-lg border transition-all duration-300 font-medium ${isActive('/exercises')
                  ? 'border-[#f97316] text-[#f97316] bg-[#f97316]/10 shadow-[0_0_15px_rgba(249,115,22,0.2)]'
                  : 'border-transparent text-gray-300 hover:text-white hover:border-[#f97316]/50'
                  }`}
              >
                Courses
                <Sparkles
                  size={14}
                  className="absolute top-0.5 right-0.5 text-orange-400 drop-shadow-[0_0_4px_rgba(251,146,60,0.9)] animate-pulse"
                />
              </Link>
            </li>

            {/* Resources Dropdown */}
            <li
              className='relative px-2 py-4 cursor-pointer group'
              onMouseEnter={handleResourcesMouseEnter}
              onMouseLeave={handleResourcesMouseLeave}
            >
              <div className={`flex items-center space-x-1 transition-colors duration-300 ${isResourcesActive() ? 'text-[#f97316]' : 'text-gray-300 group-hover:text-white'}`}>
                <span>Resources</span>
                <IoChevronDownOutline
                  className={`ml-1 transition-transform duration-300 ${resourcesOpen ? 'rotate-180' : ''}`}
                />
              </div>

              {/* Active Indicator for Dropdown Parent */}
              <span className={`absolute bottom-2 left-2 right-2 h-0.5 bg-[#f97316] transition-all duration-300 ${isResourcesActive() ? 'w-[calc(100%-16px)] shadow-[0_0_10px_#f97316]' : 'w-0'}`}></span>

              {resourcesOpen && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-80 animate-in fade-in slide-in-from-top-2 duration-200"
                >
                  <div className="bg-white dark:bg-[#0f172a]/95 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden p-2">
                    <ul className='space-y-1'>
                      <li>
                        <div
                          className={`group/item flex items-start gap-3.5 px-4 py-3.5 rounded-lg text-sm transition-all cursor-pointer ${isActive('/resources/blog') ? 'bg-orange-50 dark:bg-[#f97316]/10' : 'hover:bg-gray-50 dark:hover:bg-white/5'}`}
                          onClick={(e) => handleNavigate(e, '/resources/blog')}
                        >
                          <div className={`p-2 rounded-lg mt-0.5 transition-all ${isActive('/resources/blog') ? 'bg-orange-100 dark:bg-[#f97316]/20 text-[#f97316]' : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-white group-hover/item:bg-orange-100 dark:group-hover/item:bg-[#f97316]/20 group-hover/item:text-[#f97316] dark:group-hover/item:text-[#f97316] dark:group-hover/item:drop-shadow-[0_0_6px_rgba(251,146,60,0.7)]'}`}>
                            <BookOpen size={18} />
                          </div>
                          <div>
                            <div className={`font-semibold ${isActive('/resources/blog') ? 'text-[#f97316]' : 'text-gray-900 dark:text-white'}`}>Blog</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Guides and insights for data careers</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div
                          className={`group/item flex items-start gap-3.5 px-4 py-3.5 rounded-lg text-sm transition-all cursor-pointer ${isActive('/machine-learning') ? 'bg-orange-50 dark:bg-[#f97316]/10' : 'hover:bg-gray-50 dark:hover:bg-white/5'}`}
                          onClick={(e) => handleNavigate(e, '/machine-learning')}
                        >
                          <div className={`p-2 rounded-lg mt-0.5 transition-all ${isActive('/machine-learning') ? 'bg-orange-100 dark:bg-[#f97316]/20 text-[#f97316]' : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-white group-hover/item:bg-orange-100 dark:group-hover/item:bg-[#f97316]/20 group-hover/item:text-[#f97316] dark:group-hover/item:text-[#f97316] dark:group-hover/item:drop-shadow-[0_0_6px_rgba(251,146,60,0.7)]'}`}>
                            <Brain size={18} />
                          </div>
                          <div>
                            <div className={`font-semibold ${isActive('/machine-learning') ? 'text-[#f97316]' : 'text-gray-900 dark:text-white'}`}>Machine Learning Guides</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Learn ML concepts step by step</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div
                          className={`group/item flex items-start gap-3.5 px-4 py-3.5 rounded-lg text-sm transition-all cursor-pointer ${isActive('/resources/sql-cheatsheet') ? 'bg-orange-50 dark:bg-[#f97316]/10' : 'hover:bg-gray-50 dark:hover:bg-white/5'}`}
                          onClick={(e) => handleNavigate(e, '/resources/sql-cheatsheet')}
                        >
                          <div className={`p-2 rounded-lg mt-0.5 transition-all ${isActive('/resources/sql-cheatsheet') ? 'bg-orange-100 dark:bg-[#f97316]/20 text-[#f97316]' : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-white group-hover/item:bg-orange-100 dark:group-hover/item:bg-[#f97316]/20 group-hover/item:text-[#f97316] dark:group-hover/item:text-[#f97316] dark:group-hover/item:drop-shadow-[0_0_6px_rgba(251,146,60,0.7)]'}`}>
                            <FileCode2 size={18} />
                          </div>
                          <div>
                            <div className={`font-semibold ${isActive('/resources/sql-cheatsheet') ? 'text-[#f97316]' : 'text-gray-900 dark:text-white'}`}>SQL Cheatsheet</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Quick reference for SQL syntax</div>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div
                          className={`group/item flex items-start gap-3.5 px-4 py-3.5 rounded-lg text-sm transition-all cursor-pointer ${isActive('/data-sandbox') ? 'bg-orange-50 dark:bg-[#f97316]/10' : 'hover:bg-gray-50 dark:hover:bg-white/5'}`}
                          onClick={(e) => handleNavigate(e, '/data-sandbox')}
                        >
                          <div className={`p-2 rounded-lg mt-0.5 transition-all ${isActive('/data-sandbox') ? 'bg-orange-100 dark:bg-[#f97316]/20 text-[#f97316]' : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-white group-hover/item:bg-orange-100 dark:group-hover/item:bg-[#f97316]/20 group-hover/item:text-[#f97316] dark:group-hover/item:text-[#f97316] dark:group-hover/item:drop-shadow-[0_0_6px_rgba(251,146,60,0.7)]'}`}>
                            <FlaskConical size={18} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`font-semibold ${isActive('/data-sandbox') ? 'text-[#f97316]' : 'text-gray-900 dark:text-white'}`}>Data Sandbox</span>
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-teal-500/15 text-teal-600 dark:text-teal-400 border border-teal-500/20 leading-none">New</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Query your own CSV files in-browser</div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </li>

            <li className="relative z-50"><NavLink to="/about">About</NavLink></li>
          </ul>

          {/* Right Side Actions */}
          <div className="hidden md:flex items-center gap-3">
            {/* Search trigger */}
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400 hover:text-white transition-all text-xs min-w-[140px] lg:min-w-[200px]"
              title={`Search (${kbdShortcut})`}
            >
              <Search size={13} />
              <span className="hidden lg:inline flex-1 text-left">Search...</span>
              <span className="lg:hidden">Search</span>
              <kbd className="hidden lg:flex items-center gap-0.5 text-[9px] bg-white/10 px-1.5 py-0.5 rounded font-mono ml-auto">{kbdShortcut}</kbd>
            </button>

            <ThemeToggle />

            {/* Divider */}
            <div className="h-6 w-px bg-white/10"></div>

            {auth ? (
              <div className="flex items-center gap-2">
                {/* Dashboard button - always visible so users discover it */}
                <Link
                  to="/account"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                    isActive('/account')
                      ? 'bg-orange-500/10 border-orange-500/30 text-orange-400'
                      : 'bg-white/5 border-white/10 text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                  title="Your dashboard"
                >
                  <LayoutDashboard size={13} />
                  <span className="hidden lg:inline">Dashboard</span>
                </Link>

                {/* Avatar dropdown */}
                <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen(prev => !prev)}
                  className="flex items-center gap-1.5 group"
                  aria-label="Account menu"
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-white/10 group-hover:ring-orange-500/60 transition-all">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-[11px] font-extrabold">
                        {initials}
                      </div>
                    )}
                  </div>
                  <IoChevronDownOutline size={12} className={`text-gray-400 transition-transform duration-200 ${profileOpen ? 'rotate-180' : ''}`} />
                </button>

                {profileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-white/10 rounded-xl shadow-xl overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-gray-100 dark:border-white/5">
                      <div className="text-sm font-bold text-gray-900 dark:text-white truncate">{displayName}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</div>
                    </div>
                    <div className="p-1.5 space-y-0.5">
                      <Link
                        to="/account"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                      >
                        <FaUser size={11} /> Overview
                      </Link>
                      <Link
                        to="/account?tab=settings"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                      >
                        <FaCog size={11} /> Settings
                      </Link>
                      <button
                        onClick={async () => {
                          setProfileOpen(false);
                          try { await signOut(); } catch (e) { console.error(e); } finally { navigate('/'); }
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                      >
                        <FaSignOutAlt size={11} /> Sign Out
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="px-5 py-2.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-300 font-medium"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#f97316] to-[#fb923c] hover:from-[#ea580c] hover:to-[#f97316] text-white shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 transition-all duration-300 font-medium transform hover:-translate-y-0.5"
                >
                  Register
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Icon */}
          <div
            onClick={handleNav}
            className='block md:hidden cursor-pointer p-2 rounded-lg hover:bg-white/5 transition-colors'
            style={{ zIndex: 50 }}
          >
            {nav ?
              <AiOutlineClose size={24} className="text-[#f97316]" /> :
              <AiOutlineMenu size={24} className="text-white hover:text-[#f97316] transition-colors" />
            }
          </div>
        </div>
      </div>

      {/* Portal for Mobile Menu */}
      <MobileMenuPortal />
    </>
  );
};

export default Navbar;